import React, { useState } from "react";
import { Card, CardBody, Badge } from "reactstrap";
import { Camera, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ANSWER_OPTIONS } from "./answerOptions";

// ─────────────────────────────────────────────────────────────────────────────
// FormFillView — bir form şablonunun "Denetim Formu Doldur" sayfasındaki çizimi:
// ana soru kartı + açılır kapanır kategori kartları + cevap butonlu sorular.
//
// Üç yerde kullanılır:
//   • SahaFormFill.StepForm       → doldurma (answers / onAnswer / renderQuestionExtras)
//   • Forms/FormPreviewPage       → önizleme (readOnly, cevap butonları tıklanmaz)
//   • Forms/FormEditorPage        → satır içi düzenleme (render* slotları ile)
//
// categories: GET /inspection/generate yanıtı ile aynı şekil:
//   [{ categoryId, categoryName, questions: [{ questionId, questionName, questType,
//      photoRequiredPositive, photoRequiredNeutral }] }]
// ─────────────────────────────────────────────────────────────────────────────

const stop = (e) => e.stopPropagation();

function FormFillView({
  mainQuestion,
  mainQuestType,
  categories = [],
  answers = {},
  mainAnswer = null,
  onMainAnswer,
  onAnswer,                 // (question, category, value) => void
  questionsLocked = false,  // ana soru cevaplanana kadar kategoriler soluk ve tıklanmaz
  readOnly = false,         // önizleme: cevap butonları görünür ama tıklanmaz
  showProgress = true,      // kategori başlığında (cevaplanan/toplam); kapalıysa (toplam)
  renderMainQuestion,       // () => node — ana soru metninin yerine
  renderCategoryTitle,      // (cat, idx) => node
  renderCategoryTools,      // (cat, idx) => node — başlığın sağında
  renderQuestionTitle,      // (q, cat, idx) => node
  renderQuestionTools,      // (q, cat, idx) => node — sorunun sağında
  renderQuestionExtras,     // (q, cat, ans) => node — cevap butonlarının altında
  renderCategoryFooter,     // (cat) => node — soruların altında
}) {
  const { t } = useTranslation("saha");
  const [closedCats, setClosedCats] = useState({});

  const mainOptions = ANSWER_OPTIONS[mainQuestType] || ANSWER_OPTIONS[1];
  const inertStyle = readOnly ? { pointerEvents: "none" } : undefined;

  return (
    <>
      {/* Ana Soru */}
      <Card className="border-0 shadow-sm mb-3">
        <CardBody>
          <div className="d-flex align-items-start gap-2 mb-2">
            <i className="bx bx-question-mark text-primary fs-5 mt-1" />
            <div className="flex-grow-1">
              <div className="fw-semibold small text-uppercase text-muted mb-1">{t("sahaFormFill.anaSoru")}</div>
              {renderMainQuestion ? renderMainQuestion() : <div className="fw-semibold">{mainQuestion}</div>}
            </div>
          </div>
          <div className="d-flex gap-2 mt-2 flex-wrap" style={inertStyle}>
            {mainOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                tabIndex={readOnly ? -1 : undefined}
                className={`btn btn-sm btn-${mainAnswer === opt.value ? opt.color : "outline-" + opt.color} d-flex align-items-center gap-1`}
                onClick={() => onMainAnswer?.(opt.value)}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Kategoriler & Sorular */}
      {categories.map((cat, catIdx) => {
        const catAnswered = cat.questions.filter((q) => answers[q.questionId]).length;
        const catUygunsuz = cat.questions.filter((q) => answers[q.questionId]?.value === 2).length;
        const isOpen      = closedCats[cat.categoryId] !== true;
        const toggle      = () => setClosedCats((prev) => ({ ...prev, [cat.categoryId]: isOpen }));

        return (
          <Card key={cat.categoryId} className="border-0 shadow-sm mb-3">
            <CardBody className="p-0">
              {/* Kategori Başlığı */}
              <div
                role="button"
                tabIndex={0}
                className="btn btn-light w-100 text-start p-3 d-flex align-items-center gap-2 rounded-top border-0"
                style={{ borderRadius: "0.375rem 0.375rem 0 0" }}
                onClick={toggle}
                onKeyDown={(e) => {
                  if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); toggle(); }
                }}
              >
                <i className="bx bxs-folder-open text-primary" />
                {renderCategoryTitle ? (
                  <div className="flex-grow-1" onClick={stop} onKeyDown={stop}>{renderCategoryTitle(cat, catIdx)}</div>
                ) : (
                  <span className="fw-semibold small">{cat.categoryName}</span>
                )}
                <span className="text-muted small ms-2">
                  ({showProgress ? `${catAnswered}/${cat.questions.length}` : cat.questions.length})
                </span>
                {catUygunsuz > 0 && (
                  <Badge color="danger" pill className="ms-1">{catUygunsuz}{" "}{t("sahaFormFill.uygunsuz")}</Badge>
                )}
                {questionsLocked && (
                  <Badge color="secondary" pill className="ms-auto" style={{ fontSize: "0.65rem" }}>
                    {t("sahaFormFill.anaSoruyuCevaplayin")}
                  </Badge>
                )}
                {renderCategoryTools && (
                  <div className="ms-auto d-flex align-items-center gap-1" onClick={stop} onKeyDown={stop}>
                    {renderCategoryTools(cat, catIdx)}
                  </div>
                )}
                <span className={questionsLocked || renderCategoryTools ? "ms-1 text-muted" : "ms-auto text-muted"}>
                  {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </span>
              </div>

              {/* Sorular */}
              {isOpen && (
                <div className="border-top" style={questionsLocked ? { opacity: 0.45, pointerEvents: "none", userSelect: "none" } : {}}>
                  {cat.questions.map((q, idx) => {
                    const ans     = answers[q.questionId];
                    const isNc    = ans?.value === 2;
                    const options = ANSWER_OPTIONS[q.questType] || ANSWER_OPTIONS[1];

                    return (
                      <div
                        key={q.questionId}
                        className={`p-3 d-flex flex-column gap-2 ${idx < cat.questions.length - 1 ? "border-bottom" : ""} ${isNc ? "bg-danger bg-opacity-10" : ""}`}
                      >
                        <div className="d-flex align-items-start gap-2">
                          <span className="text-muted small mt-1" style={{ minWidth: 20 }}>{idx + 1}.</span>
                          <div className="flex-grow-1">
                            {renderQuestionTitle ? renderQuestionTitle(q, cat, idx) : (
                              <>
                                <span className="small fw-semibold">{q.questionName}</span>
                                {(q.photoRequiredPositive || q.photoRequiredNeutral) && (
                                  <Badge color="light" className="border text-muted ms-2" style={{ fontSize: "0.65rem" }}>
                                    <Camera size={10} className="me-1" />{t("sahaFormFill.fotografGerekli")}
                                  </Badge>
                                )}
                              </>
                            )}
                          </div>
                          {renderQuestionTools && (
                            <div className="d-flex align-items-center gap-1 flex-shrink-0">{renderQuestionTools(q, cat, idx)}</div>
                          )}
                        </div>

                        {/* Cevap Butonları */}
                        <div className="d-flex gap-2 flex-wrap ms-4" style={inertStyle}>
                          {options.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              tabIndex={readOnly ? -1 : undefined}
                              className={`btn btn-sm d-flex align-items-center gap-1 ${
                                ans?.value === opt.value
                                  ? `btn-${opt.color}`
                                  : `btn-outline-${opt.color}`
                              }`}
                              onClick={() => onAnswer?.(q, cat, opt.value)}
                            >
                              {opt.icon} {opt.label}
                            </button>
                          ))}
                        </div>

                        {renderQuestionExtras?.(q, cat, ans)}
                      </div>
                    );
                  })}
                  {renderCategoryFooter?.(cat)}
                </div>
              )}
            </CardBody>
          </Card>
        );
      })}
    </>
  );
}

export default FormFillView;
